"use client";

import { type ContextModalProps, modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { Icon, type SubscriptionData } from "@repo/dashboard-core";
import { useTranslations } from "next-intl";
import { useState, type ReactNode } from "react";

import {
  downSaleSubscription,
  resubscribe,
  unsubscribe,
} from "@repo/dashboard-core/actions";

import { SubscriptionConfirmation } from "./SubscriptionConfirmation";
import { SubscriptionInfoContent } from "./SubscriptionInfoContent";
import { UnsubscribeContent } from "./UnsubscribeContent";

type ViewState =
  | "subscriptionInfo"
  | "unsubscribeModal"
  | "subscriptionConfirmation"
  | "downsaleConfirmation"
  | "unsubscribeConfirmation"
  | "resubscribeConfirmation";

export type SubscriptionModalInnerProps = {
  subscriptions: SubscriptionData[];
  customerEmail: string;
};

/**
 * Opens the subscription management modal. Works from anywhere rendered
 * under the dashboard's `ModalsProvider` (see `DashboardShell`).
 */
export function openSubscriptionModal(innerProps: SubscriptionModalInnerProps) {
  if (!innerProps.subscriptions.length) return;

  modals.openContextModal({
    modal: "subscription",
    centered: true,
    overlayProps: { backgroundOpacity: 0.2, blur: 2 },
    closeButtonProps: { icon: <Icon name="close" size={18} /> },
    radius: "lg",
    size: "auto",
    styles: {
      content: { "--mantine-color-body": "#FAFAFA" } as React.CSSProperties,
    },
    innerProps,
  });
}

export function SubscriptionModal({
  context,
  id,
  innerProps,
}: ContextModalProps<SubscriptionModalInnerProps>) {
  const { subscriptions, customerEmail } = innerProps;
  const t = useTranslations("subscription");
  const [isLoading, setIsLoading] = useState(false);
  const [currentView, setCurrentView] = useState<ViewState>("subscriptionInfo");

  const subscription = subscriptions[0]!;
  const subscriptionId = subscription.subscriptionId ?? "";

  const handleClose = () => context.closeModal(id);

  async function executeAction(
    action: () => Promise<{ success?: boolean; message?: string }>,
    successMessage: string,
    successView: ViewState,
  ) {
    setIsLoading(true);
    try {
      const result = await action();
      if (result?.success) {
        notifications.show({
          title: t("notification.success-title"),
          message: successMessage,
        });
        setCurrentView(successView);
      } else {
        notifications.show({
          color: "red",
          title: t("notification.error-title"),
          message: t("notification.error"),
        });
        console.error(result?.message);
        handleClose();
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: t("notification.error-title"),
        message: t("notification.error"),
      });
      console.error(error);
      handleClose();
    } finally {
      setIsLoading(false);
    }
  }

  const handleSubscription = (acceptOffer: boolean) =>
    executeAction(
      () =>
        acceptOffer
          ? downSaleSubscription({ subscriptionId, email: customerEmail })
          : unsubscribe(subscriptionId),
      t("notification.success"),
      acceptOffer ? "downsaleConfirmation" : "unsubscribeConfirmation",
    );

  const handleResubscribe = () =>
    executeAction(
      () => resubscribe(subscriptionId),
      t("notification.resubscribe-success"),
      "resubscribeConfirmation",
    );

  const modalViews: Record<ViewState, ReactNode> = {
    subscriptionInfo: (
      <SubscriptionInfoContent
        subscription={subscription}
        setCurrentView={setCurrentView}
        onResubscribe={handleResubscribe}
        isResubscribing={isLoading}
        handleClose={handleClose}
      />
    ),
    unsubscribeModal: (
      <UnsubscribeContent
        handleSubscription={handleSubscription}
        setCurrentView={setCurrentView}
        isLoading={isLoading}
      />
    ),
    subscriptionConfirmation: (
      <SubscriptionConfirmation
        handleSubscription={handleSubscription}
        close={handleClose}
        isLoading={isLoading}
      />
    ),
    downsaleConfirmation: (
      <div className="grid grid-cols-1 gap-y-6 pb-6 lg:w-[447px]">
        <h2 className="text-xl font-semibold text-primary">
          {t("downsale-confirmation.title")}
        </h2>
        <p className="text-lg font-medium">{t("downsale-confirmation.text")}</p>
      </div>
    ),
    unsubscribeConfirmation: (
      <div className="grid grid-cols-1 gap-y-6 pb-6 lg:w-[447px]">
        <h2 className="text-xl font-semibold text-primary">
          {t("unsubscribe.title")}
        </h2>
        <p className="text-lg font-medium">{t("unsubscribe.text1")}</p>
        <p className="text-lg font-medium">{t("unsubscribe.text2")}</p>
      </div>
    ),
    resubscribeConfirmation: (
      <div className="grid grid-cols-1 gap-y-6 pb-6 lg:w-[447px]">
        <h2 className="text-xl font-semibold text-primary">
          {t("resubscribe.title")}
        </h2>
        <p className="text-lg font-medium">{t("resubscribe.text")}</p>
      </div>
    ),
  };

  return <div className="px-6">{modalViews[currentView]}</div>;
}
